import { useEffect, useState, FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { TabHeader } from '../components/TabHeader';
import { Footer } from '../components/Footer';
import { TABS_BY_SLUG } from '../tabs/tabs-data';
import { ask, AskResponse } from '../lib/ask-api';

/**
 * Answer page for the home AI-search band and every tab's per-tab
 * search strip (PRD §7.1). Reads ?q= from the URL, dispatches to the
 * ask-api client, and renders Q → A → cited sources → follow-up
 * chips. A search input at the bottom lets the visitor ask another
 * question without navigating back.
 *
 * Two mount paths:
 *   /ask?q=…            — home browser, source="home"
 *   /:slug/ask?q=…      — tab browser, source="<slug>"
 *
 * The source parameter biases the RAG retrieval toward the tab the
 * user came from — see ask-api.ts.
 */
export function AnswerPage(): JSX.Element {
  const [params] = useSearchParams();
  const { slug } = useParams();
  const q = (params.get('q') ?? '').trim();
  const source = slug ?? 'home';
  const tabTitle = slug ? TABS_BY_SLUG[slug]?.title : undefined;
  const scopeLabel = tabTitle ?? 'DI Deck';

  if (q.length === 0) {
    return <Navigate to={slug ? `/${slug}` : '/'} replace />;
  }
  if (slug && !TABS_BY_SLUG[slug]) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-sovereign-900">
      <TabHeader />
      <main className="flex-1 px-4 py-10 md:px-8 md:py-14">
        <article className="mx-auto max-w-3xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-ink-dim">
            <Link to="/" className="hover:text-brand-purple hover:underline">
              Home
            </Link>
            {slug && (
              <>
                <span className="mx-2" aria-hidden>·</span>
                <Link to={`/${slug}`} className="hover:text-brand-purple hover:underline">
                  {tabTitle}
                </Link>
              </>
            )}
            <span className="mx-2" aria-hidden>·</span>
            <span>Ask</span>
          </nav>

          <header className="mb-6">
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-brand-purple">
              Your question about {scopeLabel}
            </div>
            <h1 className="text-xl font-bold leading-snug text-ink md:text-2xl">“{q}”</h1>
          </header>

          <AnswerBody q={q} source={source} scopeLabel={scopeLabel} />

          <AskAgain slug={slug} />
        </article>
      </main>
      <Footer />
    </div>
  );
}

function AnswerBody({
  q,
  source,
  scopeLabel,
}: {
  q: string;
  source: string;
  scopeLabel: string;
}): JSX.Element {
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'done'; data: AskResponse } | { kind: 'error'; msg: string }
  >({ kind: 'loading' });
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    setState({ kind: 'loading' });
    setSlow(false);
    let cancelled = false;
    const slowTimer = setTimeout(() => {
      if (!cancelled) setSlow(true);
    }, 3000);
    ask({ q, source })
      .then((data) => {
        if (cancelled) return;
        setState({ kind: 'done', data });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          kind: 'error',
          msg: err instanceof Error ? err.message : 'Unknown error',
        });
      })
      .finally(() => {
        clearTimeout(slowTimer);
      });
    return () => {
      cancelled = true;
      clearTimeout(slowTimer);
    };
  }, [q, source]);

  if (state.kind === 'loading') return <LoadingBlock slow={slow} />;
  if (state.kind === 'error') {
    return (
      <section
        role="alert"
        className="mb-8 rounded-md border border-rag-red/40 bg-white p-4 text-sm text-ink"
      >
        The search back-end could not be reached ({state.msg}). Please email{' '}
        <a href="mailto:sales@dideck.com" className="text-brand-purple hover:underline">
          sales@dideck.com
        </a>{' '}
        for a direct answer.
      </section>
    );
  }

  const { answer, sources, followUps, stubbed } = state.data;
  return (
    <>
      <section aria-labelledby="answer" className="mb-8">
        <h2
          id="answer"
          className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-purple"
        >
          Answer
        </h2>
        <div className="rounded-md border border-sovereign-line bg-white p-5 text-[15px] leading-relaxed text-ink shadow-card">
          {answer}
          {stubbed && (
            <p className="mt-3 border-t border-sovereign-line pt-3 text-[11px] italic text-ink-dim">
              This response is served by the Sprint-4 stub back-end that ships alongside
              the front-end. The full RAG service (/api/ask Cloudflare Worker) lands in
              Sprint 5 — same wire contract, real answers grounded in the platform
              specification corpus.
            </p>
          )}
        </div>
      </section>

      {sources.length > 0 && (
        <section aria-labelledby="sources" className="mb-8">
          <h2
            id="sources"
            className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-purple"
          >
            Cited sources
          </h2>
          <ul className="space-y-2">
            {sources.map((s) => (
              <li key={s.url}>
                <Link
                  to={s.url}
                  className="block rounded-md border border-sovereign-line bg-white p-3 shadow-card transition-all hover:border-brand-purple hover:shadow-card-hover"
                >
                  <div className="text-sm font-bold text-ink">{s.title}</div>
                  <div className="mt-1 text-xs leading-snug text-ink-mute">{s.snippet}</div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {followUps.length > 0 && (
        <section aria-labelledby="follow-ups" className="mb-8">
          <h2
            id="follow-ups"
            className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-purple"
          >
            Follow-up questions about {scopeLabel}
          </h2>
          <div className="flex flex-wrap gap-2">
            {followUps.map((f) => (
              <FollowUpChip key={f} q={f} source={source} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function FollowUpChip({ q, source }: { q: string; source: string }): JSX.Element {
  const target = source === 'home' ? `/ask?q=${encodeURIComponent(q)}` : `/${source}/ask?q=${encodeURIComponent(q)}`;
  return (
    <Link
      to={target}
      className="rounded-full border border-sovereign-line bg-white px-3 py-1.5 text-xs text-ink-mute shadow-card hover:border-brand-purple hover:text-brand-purple"
    >
      {q}
    </Link>
  );
}

function LoadingBlock({ slow }: { slow: boolean }): JSX.Element {
  return (
    <section aria-live="polite" aria-busy="true" className="mb-8">
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-purple">
        Answer
      </h2>
      <div className="rounded-md border border-sovereign-line bg-white p-5 text-sm text-ink-mute shadow-card">
        <div className="flex items-center gap-2">
          <Dots />
          <span>
            {slow ? 'The AI is thinking about your question…' : 'Searching the platform specification…'}
          </span>
        </div>
      </div>
    </section>
  );
}

function Dots(): JSX.Element {
  return (
    <span aria-hidden className="inline-flex gap-1">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-purple" style={{ animationDelay: '0ms' }} />
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-purple" style={{ animationDelay: '150ms' }} />
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-purple" style={{ animationDelay: '300ms' }} />
    </span>
  );
}

/**
 * Ask-another-question input at the bottom of the answer page. Submits
 * to /ask?q=… or /:slug/ask?q=… depending on whether we're rendering
 * the home browser or a per-tab browser.
 */
function AskAgain({ slug }: { slug: string | undefined }): JSX.Element {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  function submit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = q.trim();
    if (trimmed.length === 0) return;
    const target = slug ? `/${slug}/ask?q=${encodeURIComponent(trimmed)}` : `/ask?q=${encodeURIComponent(trimmed)}`;
    nav(target);
  }
  return (
    <section aria-label="Ask another question" className="mt-10 border-t border-sovereign-line pt-6">
      <form onSubmit={submit} className="relative">
        <label htmlFor="ask-again" className="sr-only">
          Ask another question
        </label>
        <input
          id="ask-again"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask another question…"
          className="w-full rounded-md border border-sovereign-line bg-white px-4 py-3 pr-12 text-sm text-ink shadow-sm outline-none placeholder:text-ink-dim focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/25"
        />
        <button
          type="submit"
          aria-label="Ask"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-brand-purple px-2.5 py-1.5 text-white shadow-card hover:bg-brand-purple-dark"
        >
          <span aria-hidden>➤</span>
        </button>
      </form>
    </section>
  );
}
